<script setup>
import { computed, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { maxValue, minValue } from '@vuelidate/validators';
import { useFocusWithin } from '@vueuse/core';
import { getDateFromIsoString } from '../../../utils/date';

const props = defineProps({
    status: {
        type: String,
    },
    divider: {
        type: String,
        default: '/',
    },
    showDay: {
        type: Boolean,
        default: false,
    },
    name: {
        type: String,
    },
});

const emit = defineEmits(['blur-last-field', 'last-field-filled']);
const model = defineModel({ type: String });

const yearInput = ref(null);
const monthInput = ref(null);
const dayInput = ref(null);

const year = ref(getDateFromIsoString(props.modelValue).year);
const month = ref(getDateFromIsoString(props.modelValue).month);
const day = ref(getDateFromIsoString(props.modelValue).day);
const dateRefs = {
    year,
    month,
    day,
};

const focusedFields = ref({
    day: false,
    month: false,
    year: false,
});

const allFieldsWereFocused = computed(() => {
    const yearWasFocused = focusedFields.value.year || year.value?.length;
    const monthWasFocused = focusedFields.value.month || month.value?.length;
    const dayWasFocused = focusedFields.value.day || day.value?.length;

    return yearWasFocused && monthWasFocused && dayWasFocused;
});

const isEmpty = computed(() => (day.value === '' || !props.showDay) && month.value === '' && year.value === '');

const isoDate = computed(() => {
    const fullYear = String(year.value).padStart(4, '0');
    const fullMonth = String(month.value || 1).padStart(2, '0');
    try {
        const fullDay = String(day.value ?? '1').padStart(2, '0');
        return new Date(`${fullYear}-${fullMonth}-${fullDay}`).toISOString().replace('.000Z', '+00:00');
    } catch {
        return null;
    }
});

function blurLastField() {
    if (allFieldsWereFocused.value) {
        emit('blur-last-field');
    }
}

function padField(fieldName, n = 2) {
    if (!dateRefs[fieldName].value?.length) return;

    // eslint-disable-next-line no-param-reassign
    dateRefs[fieldName].value = dateRefs[fieldName].value.padStart(n, 0);
}

function limitField(fieldName, n = 2) {
    // eslint-disable-next-line no-param-reassign
    dateRefs[fieldName].value = dateRefs[fieldName].value.substring(0, n);
    padField(fieldName, n);
}

function removeNonNumericCharacters(value) {
    return value.replace(/[^0-9]/g, '');
}

function updateDay(event) {
    const { value } = event.target;
    day.value = removeNonNumericCharacters(value);

    const parsedDay = parseInt(day.value, 10);
    const dayNumber = Number.isNaN(parsedDay) ? 0 : parsedDay;

    if (dayNumber < 4 && day.value?.length < 2) return;
    if (monthInput.value?.focus) {
        monthInput.value.focus();
    } else if (yearInput.value?.focus) {
        yearInput.value.focus();
    } else {
        emit('last-field-filled');
    }

    limitField('day', 2);
}

function updateMonth(event) {
    const { value } = event.target;
    month.value = removeNonNumericCharacters(value);
    const parsedMonth = parseInt(month.value, 10);
    const monthNumber = Number.isNaN(parsedMonth) ? 0 : parsedMonth;

    if (monthNumber < 2 && month.value?.length < 2) return;
    if (yearInput.value?.focus) {
        yearInput.value.focus();
    } else {
        emit('last-field-filled');
    }

    limitField('month', 2);
}

function updateYear(event) {
    const { value } = event.target;
    year.value = removeNonNumericCharacters(value);

    if (year.value?.length < 4) return;

    limitField('year', 4);
    emit('last-field-filled');
}

function updateValue() {
    model.value = isEmpty.value ? null : isoDate.value;
}

const inputContainer = ref(null);
const { focused } = useFocusWithin(inputContainer);

watch(year, updateValue);
watch(month, updateValue);
watch(day, updateValue);
watch(model, () => {
    if (!focused.value) {
        const dateParts = getDateFromIsoString(model.value);
        year.value = dateParts.year;
        month.value = dateParts.month;
        day.value = dateParts.day;
    }
});

const focus = () => {
    if (dayInput.value) {
        dayInput.value.focus();
    } else if (monthInput.value) {
        monthInput.value.focus();
    } else if (yearInput.value) yearInput.value.focus();
};

const validations = {
    day: {
        minValue: minValue(1),
        maxValue: maxValue(31),
    },
    month: {
        minValue: minValue(1),
        maxValue: maxValue(12),
    },
};

const v$ = useVuelidate(validations, { day, month });

// Expose for usage in parent components
defineExpose({ focus });
</script>

<template>
    <div
        ref="inputContainer"
        :class="{
            [`has-status-${status}`]: status,
            [`has-status-error`]: v$.$invalid,
        }"
        class="c-formDateInput"
        @blur.capture="blurLastField"
    >
        <input
            v-if="showDay"
            :id="name"
            ref="dayInput"
            v-model.trim="day"
            aria-label="Tag"
            class="c-formDateInput__input c-formDateInput__input--day"
            data-qa="day field"
            max="31"
            min="1"
            placeholder="TT"
            type="tel"
            @blur="padField('day')"
            @focus="focusedFields.day = true"
            @input="updateDay"
        >
        <span v-if="showDay">
            {{ divider }}
        </span>
        <input
            ref="monthInput"
            v-model.trim="month"
            aria-label="Monat"
            class="c-formDateInput__input c-formDateInput__input--month"
            data-qa="month field"
            max="12"
            min="1"
            placeholder="MM"
            type="tel"
            @blur="padField('month')"
            @focus="focusedFields.month = true"
            @input="updateMonth"
        >
        <span>{{ divider }}</span>
        <input
            ref="yearInput"
            v-model.trim="year"
            aria-label="Jahr"
            class="c-formDateInput__input c-formDateInput__input--year flex-grow"
            data-qa="year field"
            placeholder="JJJJ"
            type="tel"
            @blur="padField('year', 4)"
            @focus="focusedFields.year = true"
            @input="updateYear"
        >
    </div>
</template>

<style lang="scss">
@import '~@karriereat/global-styles/scss/settings/spacing';
@import '../../../assets/scss/components/input';

$day-input-width: 1.5rem;
$month-input-width: 1.75rem;
$year-input-width: 2.25rem;

.c-formDateInput {
    $root: &;

    &:focus-within {
        border-color: var(--k-green-400);
        box-shadow: inset 0 0 0 0.09375rem var(--k-green-400);
    }

    @include input();

    display: inline-flex;

    &:not(.has-single-field) {
        width: auto;
    }

    &__input {
        box-sizing: content-box;
        padding: 0 $k-spacing--xs;
        max-width: 100%;
        border: none;

        &:first-child {
            padding-left: 0;
        }

        &:last-child {
            padding-right: 0;
        }

        &:focus {
            outline: none;
        }

        #{$root}:not(.has-single-field) & {
            &--day {
                width: $day-input-width;
                text-align: center;
            }

            &--month {
                width: $month-input-width;
                text-align: center;
            }

            &--year {
                width: $year-input-width;
            }
        }
    }
}
</style>
